import { Panel } from '@/components/admin/Field'
import DownloadMp3Button from '@/components/host/DownloadMp3Button'
import { lumi } from '@/lib/lumi'
import type { HostAccount, HostTrack } from '@/types/host'
import { MAX_HOST_TRACKS } from '@/types/host'
import { Music2, Pause, Play, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'

function PlaylistsOverview() {
  const [hosts, setHosts] = useState<HostAccount[]>([])
  const [tracks, setTracks] = useState<HostTrack[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState('')
  const [playingId, setPlayingId] = useState('')
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const h = await lumi.entities.hosts.list({
        sort: { fullName: 1 },
        limit: 100,
        projection: { fullName: 1, username: 1, active: 1 },
      })
      const all: HostTrack[] = []
      for (let page = 0; page < 10; page += 1) {
        const { list } = await lumi.entities.host_tracks.list({
          sort: { createdAt: 1 },
          limit: 500,
          skip: page * 500,
        })
        const batch = (list || []) as HostTrack[]
        all.push(...batch)
        if (batch.length < 500) break
      }
      setHosts((h.list || []) as HostAccount[])
      setTracks(all.sort((a, b) => (a.order || 0) - (b.order || 0)))
    } catch (error) {
      console.error('Falha ao carregar playlists:', error)
      toast.error('Não foi possível carregar as playlists.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    return () => {
      audioRef.current?.pause()
    }
  }, [])

  const counts = useMemo(() => {
    const map: Record<string, number> = {}
    tracks.forEach((item) => {
      map[item.hostId] = (map[item.hostId] || 0) + 1
    })
    return map
  }, [tracks])

  const visible = useMemo(
    () => (selected ? tracks.filter((item) => item.hostId === selected) : tracks),
    [tracks, selected],
  )
  const hostName = (id: string) => hosts.find((item) => item._id === id)?.fullName || 'Locutor removido'

  const togglePlay = (track: HostTrack) => {
    if (!track._id) return
    if (playingId === track._id) {
      audioRef.current?.pause()
      setPlayingId('')
      return
    }
    audioRef.current?.pause()
    const audio = new Audio(track.fileUrl)
    audio.onended = () => setPlayingId('')
    audio.onerror = () => {
      setPlayingId('')
      toast.error('Não foi possível tocar esta música.')
    }
    audioRef.current = audio
    setPlayingId(track._id)
    audio.play().catch(() => {
      setPlayingId('')
      toast.error('Não foi possível tocar esta música.')
    })
  }

  const remove = async (track: HostTrack) => {
    if (!track._id || !window.confirm('Remover esta música da playlist?')) return
    try {
      if (playingId === track._id) {
        audioRef.current?.pause()
        setPlayingId('')
      }
      await lumi.entities.host_tracks.delete(track._id)
      setTracks((prev) => prev.filter((item) => item._id !== track._id))
    } catch (error) {
      console.error('Falha ao remover música:', error)
      toast.error('Não foi possível remover a música.')
    }
  }

  return (
    <Panel title="Playlists dos locutores">
      <div className="mb-4 flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => setSelected('')}
          className={`rounded-full px-3 py-1.5 text-xs font-bold ${!selected ? 'bg-[#0b2ea8] text-white' : 'bg-slate-100 text-slate-600'}`}
        >
          Todos ({tracks.length})
        </button>
        {hosts.map((host) => (
          <button
            key={host._id}
            type="button"
            onClick={() => setSelected(host._id || '')}
            className={`rounded-full px-3 py-1.5 text-xs font-bold ${
              selected === host._id ? 'bg-[#0b2ea8] text-white' : 'bg-slate-100 text-slate-600'
            }`}
          >
            {host.fullName} ({counts[host._id || ''] || 0}/{MAX_HOST_TRACKS})
          </button>
        ))}
      </div>

      {loading ? <div className="h-20 animate-pulse rounded-xl bg-slate-100" /> : null}
      <ul className="space-y-2">
        {visible.map((track) => (
          <li key={track._id} className="flex items-center gap-2 rounded-xl border border-slate-200 p-2 sm:gap-3 sm:p-3">
            <button
              type="button"
              onClick={() => togglePlay(track)}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#ffd21f] text-[#0b2ea8]"
              aria-label={playingId === track._id ? 'Pausar' : 'Ouvir'}
            >
              {playingId === track._id ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" />}
            </button>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold">{track.title}</p>
              <p className="truncate text-xs text-slate-500">
                {track.artist ? `${track.artist} · ` : ''}
                <Music2 size={11} className="mr-0.5 inline" />
                {hostName(track.hostId)}
              </p>
            </div>
            <DownloadMp3Button track={track} />
            <button
              type="button"
              onClick={() => void remove(track)}
              className="inline-flex shrink-0 items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2.5 py-2 text-xs font-bold text-red-600 active:scale-95"
              aria-label={`Apagar ${track.title}`}
            >
              <Trash2 size={15} />
              <span className="hidden sm:inline">Apagar</span>
            </button>
          </li>
        ))}
        {!loading && !visible.length ? <p className="text-sm text-slate-500">Nenhuma música enviada ainda.</p> : null}
      </ul>
    </Panel>
  )
}

export default PlaylistsOverview
